import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { AlertCircle, MessageSquare, User, Clock, MapPin, Upload, X, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  status: string;
  priority: string;
  budget_min?: number;
  budget_max?: number;
  required_date?: string;
  created_at: string;
  customer_id: string;
  tradesman_id?: string;
  job_number: number;
}

interface Message {
  id: string;
  content: string;
  created_at: string;
  sender_id: string;
  recipient_id: string;
  message_type: string;
  profiles?: {
    full_name?: string;
    email?: string;
    user_type?: string;
  } | null;
}

interface ReportJobIssueDialogProps {
  job: Job | null;
  onClose: () => void;
}

export const ReportJobIssueDialog = ({ job, onClose }: ReportJobIssueDialogProps) => {
  const [description, setDescription] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState<string[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [participantNames, setParticipantNames] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (job) {
      fetchJobMessages();
      fetchCurrentUserProfile();
      fetchParticipantNames();
    }
  }, [job]);

  const fetchCurrentUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .single();
        
        setCurrentUserProfile(profile);
      }
    } catch (error) {
      console.error("Error fetching current user profile:", error);
    }
  };

  const fetchParticipantNames = async () => {
    if (!job) return;

    try {
      const { data, error } = await supabase.functions.invoke('get-job-participant-names', {
        body: { jobId: job.id }
      });

      if (error) throw error;
      
      setParticipantNames(data.names || {});
    } catch (error) {
      console.error("Error fetching participant names:", error);
      // Don't show error toast as this is not critical
    }
  };

  const fetchJobMessages = async () => {
    if (!job) return;

    try {
      // First get messages
      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select(`
          id,
          content,
          created_at,
          sender_id,
          recipient_id,
          message_type
        `)
        .eq("job_id", job.id)
        .order("created_at", { ascending: true });

      if (messagesError) throw messagesError;

      if (!messagesData || messagesData.length === 0) {
        setMessages([]);
        return;
      }

      // Get unique user IDs from messages
      const userIds = Array.from(new Set([
        ...messagesData.map(m => m.sender_id),
        ...messagesData.map(m => m.recipient_id)
      ].filter(Boolean)));

      // Fetch profiles for all users involved in messages
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email, user_type")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Create a map of profiles by id
      const profilesMap = new Map();
      (profilesData || []).forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      // Enrich messages with profile data
      const enrichedMessages = messagesData.map(message => ({
        ...message,
        profiles: profilesMap.get(message.sender_id) || null
      }));

      setMessages(enrichedMessages);
    } catch (error) {
      console.error("Error fetching job messages:", error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const maxFiles = 5;
    
    if (selectedFiles.length + files.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `You can only upload up to ${maxFiles} photos`,
        variant: "destructive",
      });
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file`,
          variant: "destructive",
        });
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "File too large",
          description: `${file.name} is larger than 5MB`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async () => {
    if (selectedFiles.length === 0) return [];

    setIsUploadingPhotos(true);
    const uploadedUrls: string[] = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      for (const file of selectedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${Math.random()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('report-photos')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('report-photos')
          .getPublicUrl(fileName);

        uploadedUrls.push(publicUrl);
      }

      setUploadedPhotoUrls(uploadedUrls);
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload photos. Please try again.",
        variant: "destructive",
      });
      return [];
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job || !description.trim() || !currentUserProfile) return;

    setIsSubmitting(true);

    try {
      // Upload photos first if any are selected
      const photoUrls = await uploadPhotos();

      // Send report via edge function
      const { error } = await supabase.functions.invoke('send-job-issue-report', {
        body: {
          job,
          messages,
          reporterProfile: currentUserProfile,
          issueDescription: description,
          evidenceUrls: photoUrls,
        }
      });

      if (error) throw error;

      toast({
        title: "Report submitted",
        description: "Your job issue report has been sent to administrators.",
      });

      setDescription("");
      onClose();
    } catch (error: any) {
      console.error("Error submitting report:", error);
      toast({
        title: "Error submitting report",
        description: error.message || "Failed to submit report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!job) return null;

  return (
    <Dialog open={!!job} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Report Job Issue
          </DialogTitle>
          <DialogDescription>
            Report issues with this job to administrators. All job details and messages will be included in the report.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>Title:</strong> {job.title}
                </div>
                <div>
                  <strong>Unique Job ID:</strong> {job.id.substring(0, 8)}
                </div>
                <div>
                  <strong>Status:</strong> 
                  <Badge className="ml-2" variant="secondary">
                    {job.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <strong>Priority:</strong> 
                  <Badge className="ml-2" variant="outline">
                    {job.priority}
                  </Badge>
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <MapPin className="h-4 w-4" />
                  <strong>Location:</strong>
                </div>
                <p className="text-sm text-muted-foreground">{job.location}</p>
              </div>

              <div>
                <strong>Description:</strong>
                <p className="text-sm text-muted-foreground mt-1">{job.description}</p>
              </div>

              {(job.budget_min || job.budget_max) && (
                <div>
                  <strong>Budget:</strong> 
                  <span className="ml-2">
                    {job.budget_min && job.budget_max 
                      ? `£${job.budget_min} - £${job.budget_max}`
                      : job.budget_min 
                        ? `From £${job.budget_min}`
                        : `Up to £${job.budget_max}`
                    }
                  </span>
                </div>
              )}

              {job.required_date && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <strong>Required Date:</strong>
                  <span className="ml-2">{format(new Date(job.required_date), 'PPP')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Job Messages ({messages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-muted-foreground text-sm">No messages for this job yet.</p>
              ) : (
                <ScrollArea className="h-64 w-full border rounded-lg p-4">
                  <div className="space-y-4">
                     {messages.map((message, index) => {
                       const isCurrentUser = currentUserProfile && message.sender_id === currentUserProfile.id;
                       const senderName = isCurrentUser ? 'You' : (participantNames[message.sender_id] || 'User');
                       const userType = message.profiles?.user_type || 'user';
                       
                       return (
                        <div key={message.id} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] ${isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-3 py-2`}>
                             <div className="flex items-center gap-2 mb-1">
                               <span className="text-xs font-medium">
                                 {senderName}
                              </span>
                              <Badge 
                                variant={userType === 'customer' ? 'default' : 'secondary'} 
                                className="text-xs h-4"
                              >
                                {userType === 'customer' ? 'Customer' : 'Tradesman'}
                              </Badge>
                            </div>
                            <p className="text-sm">
                              {message.message_type === 'photo' ? (
                                <span className="flex items-center gap-1 italic">
                                  <Camera className="h-3 w-3" />
                                  Photo message
                                </span>
                              ) : (
                                message.content
                              )}
                            </p>
                            <div className="text-xs opacity-70 mt-1">
                              {format(new Date(message.created_at), 'MMM dd, HH:mm')}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Issue Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Describe the issue you're experiencing with this job *
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide details about the issue you're reporting..."
              rows={4}
              required
              className="resize-none"
            />
          </div>

          {/* Evidence Photos */}
          <div className="space-y-3">
            <Label>Evidence Photos (Optional - Up to 5 photos)</Label>
            
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
              <div className="text-center">
                <input
                  type="file"
                  id="photo-upload"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={selectedFiles.length >= 5}
                />
                <label
                  htmlFor="photo-upload"
                  className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    selectedFiles.length >= 5
                      ? 'bg-muted text-muted-foreground border-muted cursor-not-allowed'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  <Camera className="h-4 w-4" />
                  {selectedFiles.length >= 5 ? 'Maximum photos selected' : 'Select Photos'}
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  PNG, JPG up to 5MB each. Maximum 5 photos.
                </p>
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium">Selected Photos ({selectedFiles.length}/5):</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2">
                          <Camera className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm truncate max-w-32" title={file.name}>
                            {file.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="destructive" 
              disabled={isSubmitting || !description.trim()}
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};